import _ from "lodash";
import React, { useEffect, useRef } from "react";

// custom hooks
import { useI18n } from "Hooks";
import { useHandleScroll } from "Utils/CustomHooks";

// components
import Loading from "UIComponents/Loading";

// modules
import { updateAssessmentInRedux } from "Assessments/modules/AssessmentModule";

// design system
import {
  EmptyState,
  RadioButton,
  SearchInput,
  Tooltip,
} from "@toddle-edu/ds-web";
import { NoSearchResultsIllustration } from "@toddle-edu/ds-theme";

// third party
import classNames from "classnames";
import { connect } from "react-redux";

// utils
import { onEnterOrSpaceKeyDown } from "Utils";

// scss
import classes from "./UnitSelectionList.scss";

const styles = {
  overlayInnerStyle: {
    maxWidth: "280px",
  },
};

const UnitSelectionList = props => {
  const {
    unitSearchText,
    handleSearchTextChange,
    unitPlanOptions,
    isLoading,
    isLoadingMoreUnitPlans,
    hasNextPage,
    loadMoreUnitPlanFeed,
    networkStatus,
    handleUnitSelection,
    selectedUnitPlan,
  } = props;

  const { t } = useI18n();
  const scrollContainerRef = useRef();

  useEffect(() => {
    const { updateAssessmentInRedux } = props;
    return () => updateAssessmentInRedux({ unitSearchText: "" });
  }, []);

  useHandleScroll({
    networkStatus,
    loadMoreFeed: loadMoreUnitPlanFeed,
    hasNextPage,
    getScrollRef: () => scrollContainerRef.current,
    // sometimes ref is undefined and pagination doesn't work
    // on first open, passing hookDependencies fixes that
    hookDependencies: [scrollContainerRef.current],
  });

  const onSelectUnit = id => {
    if (selectedUnitPlan?.value === id) {
      handleUnitSelection("");
    } else {
      handleUnitSelection(id);
    }
  };

  const renderLoading = () => {
    return (
      <div className={classes.loadingContainer}>
        <Loading />
      </div>
    );
  };

  const renderBody = () => {
    if (isLoading) return renderLoading();
    else if (_.isEmpty(unitPlanOptions)) {
      return (
        <EmptyState
          illustration={NoSearchResultsIllustration}
          size={"large"}
          dsVersion="2.0"
          title={t("unitPlan:no_all_units_found")}
        />
      );
    } else {
      return _.map(unitPlanOptions, unitPlan => {
        const { value: id, label, subLabel, backgroundImage } = unitPlan;

        const isUnitSelected = selectedUnitPlan?.value === id;

        const unitPlanCardClass = classNames(classes.unitPlanCard, {
          [classes.selectedUnitPlanCard]: isUnitSelected,
        });

        return (
          <div
            key={id}
            className={unitPlanCardClass}
            onClick={() => onSelectUnit(id)}
            onKeyDown={e => onEnterOrSpaceKeyDown(e, () => onSelectUnit(id))}
            role={"button"}
            tabIndex={0}
            aria-label={"unit plan card"}
          >
            <div className={classes.cardLeft}>
              <div className={classes.unitIcon}>
                {backgroundImage ? (
                  <div
                    className={classes.unitCoverImage}
                    style={{ backgroundImage }}
                  />
                ) : null}
              </div>

              <div className={classes.unitInfo}>
                <span className={classNames(classes.unitTitle, "text-body")}>
                  {label}
                </span>

                <Tooltip
                  dsVersion="2.0"
                  tooltip={subLabel}
                  placement="top"
                  showArrow={true}
                  overlayInnerStyle={styles.overlayInnerStyle}
                >
                  <span
                    className={classNames(classes.unitSubTitle, "text-body-s")}
                  >
                    {subLabel}
                  </span>
                </Tooltip>
              </div>
            </div>

            <RadioButton
              dsVersion="2.0"
              size="medium"
              checked={isUnitSelected}
            />
          </div>
        );
      });
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.searchContainer}>
        <SearchInput
          value={unitSearchText}
          onChange={handleSearchTextChange}
          dsVersion="2.0"
          size={"medium"}
          focusSearchBySlash={true}
          placeholder={t("common:search_placeholder")}
        />
      </div>

      <div className={classes.scrollContainer} ref={scrollContainerRef}>
        <div className={classes.unitPlansContainer}>
          {renderBody()}
          {isLoadingMoreUnitPlans ? renderLoading() : null}
        </div>
      </div>
    </div>
  );
};

const mapActionCreators = {
  updateAssessmentInRedux,
};

export default connect(null, mapActionCreators)(UnitSelectionList);
